"use client";
import { useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";

import { useApi } from "@/lib/fetch-client";
import { clearPendingIdentity, identifyUser } from "@/lib/posthog-identity";

export interface UserUpdateData {
	name?: string;
	email?: string;
}

export interface PasswordUpdateData {
	currentPassword: string;
	newPassword: string;
}

export interface UseUserOptions {
	redirectTo?: string;
	redirectWhen?: "authenticated" | "unauthenticated";
}

export function useUser(options?: UseUserOptions) {
	const posthog = usePostHog();
	const router = useRouter();
	const api = useApi();
	const pathname = usePathname();

	const { data, isLoading, error } = api.useQuery(
		"get",
		"/user/me",
		{},
		{
			retry: 0,
			staleTime: 5 * 60 * 1000, // 5 minutes
			refetchOnWindowFocus: false,
		},
	);

	useEffect(() => {
		if (data?.user) {
			// Queued rather than called directly: this effect fires once and can
			// win the race against the deferred posthog.init(), which would drop
			// the identify() and leave the whole session anonymous.
			identifyUser(posthog, data.user.id, {
				email: data.user.email,
				name: data.user.name,
			});
		} else {
			clearPendingIdentity();
		}
	}, [data?.user, posthog]);

	// Check for onboarding completion for all authenticated users
	useEffect(() => {
		if (!data?.user || isLoading) {
			return;
		}

		const currentPath = pathname;
		const isAuthPage = ["/login", "/signup"].includes(currentPath);
		const isLandingPage = currentPath === "/";

		// Don't redirect if already on auth pages
		if (isAuthPage || isLandingPage) {
			// No action needed for auth/landing pages
		}
	}, [data?.user, isLoading, router, pathname]);

	// Handle existing redirect logic
	useEffect(() => {
		if (!options?.redirectTo || !options?.redirectWhen) {
			return;
		}

		const { redirectTo, redirectWhen } = options;
		const hasUser = !!data?.user;

		if (redirectWhen === "authenticated" && hasUser) {
			router.push(redirectTo);
		} else if (
			redirectWhen === "unauthenticated" &&
			!isLoading &&
			(!hasUser || error)
		) {
			router.push(redirectTo);
		}
	}, [
		data?.user,
		isLoading,
		error,
		router,
		options?.redirectTo,
		options?.redirectWhen,
		options,
	]);

	return {
		user: data?.user ?? null,
		isLoading,
		error,
		data,
	};
}

export function useUpdateUser() {
	const queryClient = useQueryClient();
	const api = useApi();

	return api.useMutation("patch", "/user/me", {
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: ["user"] });
			void queryClient.invalidateQueries({ queryKey: ["session"] });
		},
	});
}

export function useUpdatePassword() {
	const api = useApi();
	return api.useMutation("put", "/user/password");
}

export function useDeleteAccount() {
	const api = useApi();
	return api.useMutation("delete", "/user/me");
}
